import { useEffect, useState } from "react";
import z from "zod";
import { ConversationData } from "../types/types";
import { ConversationDataSchema, ConversationSchema } from "../types/schemas";
import { getAvatarId, useJwt } from "../../../../../utils/jwt";

const VELVET_GLITCH_CACHE_BUST_KEY = "v1";

export function useConversationData({
    lang,
    conversationName,
}: {
    lang: string;
    conversationName: string;
}) {
    if (lang === undefined || lang === null || lang === "") {
        lang = "en";
    }

    const [conversationData, setConversationData] = useState<
        ConversationData["conversation"] | null
    >(null);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    const jwt = useJwt();

    useEffect(() => {
        (async () => {
            const avatarId = getAvatarId(jwt ?? "") ?? "";

            const cache = localStorage.getItem(
                `velvet-glitch-conversation-${conversationName}-${avatarId}-${lang}`,
            );

            if (cache) {
                try {
                    const parsedCacheData = JSON.parse(cache);

                    const validationResult = z
                        .object({
                            conversation: ConversationSchema,
                            cacheBustVersion: z.string(),
                        })
                        .safeParse(parsedCacheData);

                    if (validationResult.success) {
                        const parsedCache = validationResult.data;

                        console.log("Using cached conversation data");

                        setConversationData(parsedCache.conversation);
                        setLoading(false);
                        setError(false);
                        return;
                    } else {
                        console.error(
                            "Failed to validate cached conversation data:",
                            validationResult.error,
                        );
                    }
                } catch (error) {
                    console.error(
                        "Failed to parse cached conversation data",
                        error,
                    );
                }
            }

            const res = await fetch(
                `${window.CONVERSATIONS_API_BASE}/get-conversation?lang=${lang}&conversationName=${conversationName}`,
                {
                    method: "GET",
                },
            );
            if (res.status !== 200) {
                setError(true);
                setLoading(false);
                throw new Error(
                    `Failed to fetch conversation data: ${res.status}`,
                );
            }
            const data = await res.json();

            const validationResult = ConversationDataSchema.safeParse(data);

            if (!validationResult.success) {
                setError(true);
                setLoading(false);
                console.error(
                    "Failed to validate conversation data:",
                    validationResult.error,
                );
                return;
            }

            const conversation = validationResult.data.conversation;

            setError(false);
            setLoading(false);
            setConversationData(conversation);

            localStorage.setItem(
                `velvet-glitch-conversation-${conversationName}-${avatarId}-${lang}`,
                JSON.stringify({
                    cacheBustVersion: VELVET_GLITCH_CACHE_BUST_KEY,
                    conversation,
                }),
            );
        })().catch((error) => {
            console.error("Error fetching conversation data:", error);
            setError(true);
            setLoading(false);
        });
    }, [jwt]);

    return { conversation: conversationData, error, loading };
}
